const normalizeRoleName = (user) => (user?.roleName || user?.nombreRol || user?.role || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const compactRoleName = (user) => normalizeRoleName(user).replace(/\s+/g, '');

const isAdminRegional = (user) => {
  const roleName = normalizeRoleName(user);
  const compactRole = compactRoleName(user);
  return compactRole.includes('adminregional')
    || compactRole.includes('admindelregional')
    || roleName.includes('admin regional');
};

const isAdminArea = (user) => {
  const roleName = normalizeRoleName(user);
  const compactRole = compactRoleName(user);
  return compactRole.includes('adminarea')
    || compactRole.includes('admindearea')
    || compactRole.includes('admindelarea')
    || compactRole.includes('admindpto')
    || compactRole.includes('admindepartamento')
    || roleName.includes('admin area')
    || roleName.includes('admin de area');
};

const isGeneralAdmin = (user) => {
  const roleName = normalizeRoleName(user);
  return roleName.includes('admin') && !isAdminRegional(user) && !isAdminArea(user);
};

const isAdminUser = (user) => isGeneralAdmin(user) || isAdminRegional(user) || isAdminArea(user);

const isProfesor = (user) => {
  const roleName = normalizeRoleName(user);
  return roleName === 'profesor' || roleName.includes('profesor') || roleName.includes('profe');
};

const normalizeFlagName = (value = '') => String(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');

const isActiveFlag = (flag) => {
  const value = flag?.activo ?? flag?.Activo;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'si' || normalized === 'sí';
  }
  return false;
};

const isGptClassicEnabled = (flags = []) => {
  if (!Array.isArray(flags)) return false;
  return flags.some((flag) => {
    const nombre = normalizeFlagName(flag?.nombre || flag?.Nombre || '');
    return nombre === 'asistentesclasicosgpt' && isActiveFlag(flag);
  });
};

const isOpenAIAssistantEnabled = (flags = []) => {
  if (!Array.isArray(flags)) return false;
  return flags.some((flag) => {
    const nombre = normalizeFlagName(flag?.nombre || flag?.Nombre || '');
    const matchesKnownFlag = [
      'asistentesopenai',
      'asistenteopenai',
      'asistentesiaopenai',
      'crearasisstentesopenai',
      'crearasistentesopenai',
      'crearasistenteopenai',
      'permitecrearasistentesopenai',
      'permitecrearasistenteopenai',
      'permiteasistentesopenai'
    ].includes(nombre);
    const matchesDescriptiveFlag =
      nombre.includes('openai') &&
      nombre.includes('asistente') &&
      !nombre.includes('historial') &&
      !nombre.includes('clasico');

    return isActiveFlag(flag) && (matchesKnownFlag || matchesDescriptiveFlag);
  });
};

const isRagFlowAssistantEnabled = (flags = []) => {
  if (!Array.isArray(flags)) return false;
  return flags.some((flag) => {
    const nombre = normalizeFlagName(flag?.nombre || flag?.Nombre || '');
    const matchesKnownFlag = [
      'asistentesragflow',
      'asistenteragflow',
      'asistentesiaragflow',
      'crearasistentesragflow',
      'crearasistenteragflow',
      'permitecrearasistentesragflow',
      'permitecrearasistenteragflow',
      'permiteasistentesragflow'
    ].includes(nombre);
    const matchesDescriptiveFlag =
      nombre.includes('ragflow') &&
      nombre.includes('asistente');

    return isActiveFlag(flag) && (matchesKnownFlag || matchesDescriptiveFlag);
  });
};

const canAccessGptClassic = (user, gptClassicEnabled = false) => Boolean(user) && (gptClassicEnabled || isAdminUser(user));
const canAccessOpenAIAssistant = (user, openAIAssistantEnabled = false) => Boolean(user) && (openAIAssistantEnabled || isAdminUser(user));
const canAccessRagFlowAssistant = (user, ragFlowAssistantEnabled = false) => Boolean(user) && ragFlowAssistantEnabled;

const filterAdminMenuItems = (items, optionsOrUser) => {
  if (!Array.isArray(items)) return [];
  const options = optionsOrUser && Object.prototype.hasOwnProperty.call(optionsOrUser, 'user')
    ? optionsOrUser
    : { user: optionsOrUser, isGptClassicEnabled: false };
  return items.filter((item) => item.id !== 'asistentes-gpt' || canAccessGptClassic(options.user, options.isGptClassicEnabled));
};

export {
  canAccessOpenAIAssistant,
  canAccessRagFlowAssistant,
  canAccessGptClassic,
  filterAdminMenuItems,
  isAdminArea,
  isAdminRegional,
  isAdminUser,
  isGeneralAdmin,
  isGptClassicEnabled,
  isOpenAIAssistantEnabled,
  isRagFlowAssistantEnabled,
  isProfesor,
  normalizeRoleName
};
